import { type Draft, produce } from 'immer';
import { AsyncStatus } from 'lib-shared-types-api';

// ============================================================================
// State
// ============================================================================

/** Consolidated state managed by the reducer. */
export type UseLazyReadSearchInfiniteQueryState<TResult, TMeta> = {
  readonly results: readonly TResult[];
  readonly meta: TMeta | undefined;
  readonly asyncStatus: AsyncStatus;
  readonly isFetchingNextPage: boolean;
  readonly isFetchingPreviousPage: boolean;
};

// ============================================================================
// Actions
// ============================================================================

export type UseLazyReadSearchInfiniteQueryAction<TResult, TMeta> =
  | {
      type: 'fetchStarted';
      direction?: 'next' | 'previous';
    }
  | {
      type: 'fetchSucceeded';
      results: readonly TResult[];
      meta: TMeta;
      infinite: boolean;
      direction?: 'next' | 'previous';
    }
  | { type: 'fetchFailed' }
  | { type: 'updateResults'; recipe: (draft: TResult[]) => void };

// ============================================================================
// Initial state
// ============================================================================

export function createInitialState<
  TResult,
  TMeta,
>(): UseLazyReadSearchInfiniteQueryState<TResult, TMeta> {
  return {
    results: [],
    meta: undefined,
    asyncStatus: 'uninitialized',
    isFetchingNextPage: false,
    isFetchingPreviousPage: false,
  };
}

// ============================================================================
// Reducer
// ============================================================================

export function useLazyReadSearchInfiniteQueryReducer<TResult, TMeta>(
  state: UseLazyReadSearchInfiniteQueryState<TResult, TMeta>,
  action: UseLazyReadSearchInfiniteQueryAction<TResult, TMeta>,
): UseLazyReadSearchInfiniteQueryState<TResult, TMeta> {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'fetchStarted':
        draft.asyncStatus = 'pending';
        draft.isFetchingNextPage = action.direction === 'next';
        draft.isFetchingPreviousPage = action.direction === 'previous';
        break;

      case 'fetchSucceeded':
        if (!action.infinite || !action.direction) {
          draft.results = action.results as Draft<TResult>[];
        } else if (action.direction === 'next') {
          (draft.results as TResult[]).push(...(action.results as TResult[]));
        } else {
          (draft.results as TResult[]).unshift(
            ...(action.results as TResult[]),
          );
        }

        draft.meta = action.meta as Draft<TMeta>;
        draft.asyncStatus = 'fulfilled';
        draft.isFetchingNextPage = false;
        draft.isFetchingPreviousPage = false;
        break;

      case 'fetchFailed':
        draft.asyncStatus = 'rejected';
        draft.isFetchingNextPage = false;
        draft.isFetchingPreviousPage = false;
        break;

      case 'updateResults':
        action.recipe(draft.results as TResult[]);
        break;
    }
  });
}
